import * as utils from "@/utils";
import type {
  SdkCallback,
  SdkMethodHandler,
  SdkBridgeMethods,
  MessageEvent,
  OpenLinkOptions,
} from "./types";

const isWebTp = +utils.getUrlParams("isWebTp") === 1;
const IS_ANDROID = utils.getUrlParams("devicePlate") == "android";
const addEvenListener = function (
  callback: (e: MessageEvent) => void,
  eventName: string
): void {
  let _addEvenListener =
    window.addEventListener || (window as any).attatchEvent;
  let messageEvent = window.addEventListener ? eventName : "on" + eventName;
  _addEvenListener(messageEvent, callback, false);
};

if (isWebTp) {
  addEvenListener(function (e: MessageEvent) {
    let evt = e || (window as any).event;
    let data = evt.data;
    if (data.processFn && +data.processFn === 1) {
      if (
        Object.prototype.toString.call(
          window[data.processFnName as keyof Window]
        ) === "[object Function]"
      ) {
        (window[data.processFnName as keyof Window] as Function)(data.params);
      }
    }
  }, "message");
}

interface SdkBridge {
  methods: SdkBridgeMethods;
  bridgeReadyCallback: SdkCallback | string;
  genteratecallbackName(): string;
  gernerateNewCallback(
    callback: SdkCallback,
    globalCallbackName: string
  ): SdkCallback;
  callSdkMethod(
    method: string,
    params: Record<string, any> | SdkCallback,
    callback?: SdkCallback
  ): void;
  runInsdk(): boolean;
  runInsdkLandscape(): boolean;
  runInsdkIOS(): boolean;
  runInAndroid(): boolean;
  createReadyEvent(): void;
  listendRendy(callback: SdkCallback): void;
  injectObjFinish(): boolean;
  onReady(callback: SdkCallback): void;
  callJavascriptMethod(method: string, paramsStr: string): void;
  registHandler(methodName: string, handler: SdkMethodHandler): void;
  open(
    link: string,
    loginDir?: number,
    appendParams?: number,
    commonParams?: number,
    openWebview?: number,
    openSelf?: number,
    pageOrientation?: number
  ): void;
  openInline(link: string): void;
  closeWebview(): void;
}

const sdkBridge: SdkBridge = {
  methods: {},
  bridgeReadyCallback: "",

  genteratecallbackName(): string {
    let timestamp = new Date().getTime();
    return "sdkCallBack" + timestamp;
  },

  gernerateNewCallback(
    callback: SdkCallback,
    globalCallbackName: string
  ): SdkCallback {
    return function (this: any, ...args: any[]) {
      let res = callback.apply(this, args);
      delete (window as any)[globalCallbackName];
      return res;
    };
  },

  callSdkMethod(
    method: string,
    params: Record<string, any> | SdkCallback,
    callback?: SdkCallback
  ): void {
    if (typeof params == "function") {
      callback = params as SdkCallback;
      params = {};
    }

    if (typeof window !== "undefined" && typeof callback === "function") {
      let globalCallbackName = this.genteratecallbackName();
      while ((window as any)[globalCallbackName]) {
        globalCallbackName = this.genteratecallbackName();
      }
      // 全局声明一个临时函数地址，地址指向回调函数。执行回调之后，会自动删除这个全局函数。
      (window as any)[globalCallbackName] = this.gernerateNewCallback(
        callback,
        globalCallbackName
      );
      (params as Record<string, any>)["callback"] = globalCallbackName;
    }
    let paramsStr = JSON.stringify(params);

    // andoid
    if (
      typeof window !== "undefined" &&
      window.sdkCtrl &&
      typeof window.sdkCtrl.callSDKMethodWithParameters == "function"
    ) {
      window.sdkCtrl.callSDKMethodWithParameters(method, paramsStr);
    }

    // ios
    else if (
      typeof window !== "undefined" &&
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.callSDKMethodWithParameters
    ) {
      window.webkit.messageHandlers.callSDKMethodWithParameters.postMessage({
        method,
        params,
      });
    }
    // h5弹屏
    else if (+utils.getUrlParams("isWebTp") === 1) {
      window.parent.postMessage(
        {
          method: method,
          param: paramsStr || "{}",
        },
        "*"
      );
    }
  },

  runInsdk(): boolean {
    let runInAndroid =
      typeof window !== "undefined" &&
      window.sdkCtrl &&
      typeof window.sdkCtrl.callSDKMethodWithParameters == "function";
    let runInIOS =
      typeof window !== "undefined" &&
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.callSDKMethodWithParameters;
    return runInAndroid || runInIOS;
  },

  runInsdkLandscape(): boolean {
    return this.runInsdk() && utils.isMobileLandscape();
  },

  runInsdkIOS(): boolean {
    return (
      typeof window !== "undefined" &&
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.callSDKMethodWithParameters
    );
  },

  runInAndroid(): boolean {
    return (
      typeof window !== "undefined" &&
      window.sdkCtrl &&
      typeof window.sdkCtrl.callSDKMethodWithParameters == "function"
    );
  },

  createReadyEvent(): void {
    if (
      typeof window !== "undefined" &&
      typeof this.bridgeReadyCallback === "function"
    ) {
      // 原生app 在装载页面成功后，触发 window.triggerBridgeReady 告诉前端，前端可以调用原声的方法.
      window.triggerBridgeReady = function (this: SdkBridge) {
        (this.bridgeReadyCallback as SdkCallback)();
      }.bind(this);
    }
  },

  listendRendy(callback: SdkCallback): void {
    this.bridgeReadyCallback = callback;
    if (typeof window.triggerBridgeReady != "function") {
      this.createReadyEvent();
    }
  },

  injectObjFinish(): boolean {
    let androidSuccess =
      window &&
      window.sdkCtrl &&
      typeof window.sdkCtrl.callSDKMethodWithParameters == "function";
    let iosSuccesss =
      typeof window !== "undefined" &&
      window.webkit &&
      window.webkit.messageHandlers &&
      window.webkit.messageHandlers.callSDKMethodWithParameters;
    return androidSuccess || iosSuccesss;
  },

  onReady(callback: SdkCallback): void {
    if (this.injectObjFinish()) {
      if (typeof callback == "function") {
        callback(this);
      }
    } else {
      this.listendRendy(callback);
    }
  },

  callJavascriptMethod(method: string, paramsStr: string): void {
    let params: Record<string, any> = {};
    if (paramsStr) {
      params = JSON.parse(paramsStr);
    }
    // 构造回调函数
    let callback = function (this: SdkBridge, ...args: any[]) {
      let callbackMethodName = params["callback"];
      if (params["callback"]) {
        let ars = Array.prototype.slice.call(arguments, 0);
        ars.unshift(callbackMethodName);
        this.callSdkMethod.apply(this, ars);
      }
    };
    if (typeof this.methods[method] === "function") {
      this.methods[method](params, callback.bind(this));
    }
  },

  registHandler(methodName: string, handler: SdkMethodHandler): void {
    if (
      typeof window !== "undefined" &&
      window.callJavascriptMethod === undefined
    ) {
      window.callJavascriptMethod = this.callJavascriptMethod.bind(this);
    }
    // 不能重复注册，覆盖了其他函数
    if (typeof this.methods[methodName] === "function") {
      throw new Error(
        `MethodsName '${methodName}' was registed ,plz change another`
      );
    }
    // 注册时必须有函数实体
    if (typeof handler !== "function") {
      throw new Error("The second argument must be a callback function");
    }
    this.methods[methodName] = handler;
  },

  open(
    link: string,
    loginDir: number = 1,
    appendParams: number = 1,
    commonParams: number = 1,
    openWebview: number = 1,
    openSelf: number = 0,
    pageOrientation: number = 0
  ): void {
    // 如果链接未配置，直接不响应
    if (!link) return;
    // 如果是 Electron 环境则置为 1
    if (utils.getDeviceOld().isElectron()) {
      openSelf = 1;
    }
    if (commonParams) {
      link = window.globalGetGotoLink?.({ link }) || link;
    }
    if (this.runInsdk()) {
      let callName = openWebview ? "openWebView" : "openBrowser";
      this.callSdkMethod(callName, {
        url: link,
        loginDir, // 是否登录
        appendParams, // 是否携带参数
        pageOrientation, // 是否横屏
      });
    } else if (/MiniProgram/.test(window.navigator.userAgent)) {
      window.parent.postMessage(
        {
          action: "openDanaWebview",
          param: link,
        },
        "*"
      );
    } else if (
      window.location.href.includes("pt=tiktok") ||
      /MiniProgram/.test(window.navigator.userAgent)
    ) {
      if (!(link.includes("https://") || link.includes("http://"))) {
        link = `${window.location.protocol}//${link}`;
      }
      const url = new URL(link);
      url.searchParams.set("pt", utils.getUrlParams("pt"));
      url.searchParams.set(
        "passportToken",
        utils.getUrlParams("passportToken")
      );
      window.parent.postMessage(
        {
          action: "openDanaWebview",
          param: url.toString(),
        },
        "*"
      );
    } else if (
      new URL(window.location.href)?.searchParams
        ?.get?.("scid")
        ?.includes?.("shortcut")
    ) {
      window.open(link, "_self");
    } else {
      window.open(link, openSelf ? "_self" : "_blank");
    }
  },

  openInline(link: string): void {
    link = window.globalGetGotoLink?.({ link }) || link;
    window.location.href = link;
  },

  closeWebview(): void {
    this.callSdkMethod("closeWebview");
  },
};

export default sdkBridge;
